import React, { useState, useEffect } from 'react';
import './ServerError.css';
import { serverErrorAPI } from '../../../config/server-api';
import { toast } from 'react-toastify';
import { Copy, X, Activity, CheckCircle, AlertCircle, Clock, List, Server, Smartphone } from 'lucide-react';

const ERROR_TABS = {
  backend: 'backend',
  client: 'client',
};

const formatJsonBlock = (value) => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }

  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return '{}';
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const hasJsonContent = (value) => formatJsonBlock(value) != null;

const JsonDetailSection = ({ label, data, small = false }) => {
  const formatted = formatJsonBlock(data);
  if (formatted == null) return null;

  return (
    <div className="server-error-detail-box">
      <div className="server-error-json-container">
        <div className="server-error-json-header">
          <span>{label}</span>
        </div>
        <pre className={`server-error-json-view ${small ? 'small' : ''}`}>{formatted}</pre>
      </div>
    </div>
  );
};

const ServerError = ({ refreshTrigger }) => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filters, setFilters] = useState({
    dateFilter: "all",
    startDate: '',
    endDate: ''
  });

  const [firstLoad, setFirstLoad] = useState(true);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [monitoringData, setMonitoringData] = useState(null);
  const [isMonitorModalOpen, setIsMonitorModalOpen] = useState(false);
  const [loadingMonitor, setLoadingMonitor] = useState(false);
  const [activeTab, setActiveTab] = useState(ERROR_TABS.backend);

  const fetchErrors = async (page = 1, dateFilter = 'all', startDate = '', endDate = '', tab = activeTab) => {
    const isClientTab = tab === ERROR_TABS.client;
    const failLabel = isClientTab ? 'client error reports' : 'server errors';

    try {
      setLoading(true);
      setRequestError(null);

      const response = isClientTab
        ? await serverErrorAPI.getClientErrorReports(page, 20, dateFilter, startDate, endDate)
        : await serverErrorAPI.getServerErrors(page, 20, dateFilter, startDate, endDate);

      if (response.success) {
        setErrors(response.data || []);
        setPagination(response.pagination || {});
      } else {
        setRequestError(response.message || `Failed to fetch ${failLabel}`);
        toast.error(response.message || `Failed to fetch ${failLabel}`);
      }
    } catch (error) {
      console.error(`Error fetching ${failLabel}:`, error);
      setRequestError(`Error fetching ${failLabel}: ${error.message}`);
      toast.error(`Error fetching ${failLabel}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilters(prev => ({ ...prev, dateFilter: 'all' }));
    fetchErrors(1, 'all', '', '', ERROR_TABS.backend);
    setFirstLoad(false);
  }, []);

  useEffect(() => {
    if (firstLoad) return;
    if (pagination.currentPage !== currentPage) {
      fetchErrors(currentPage, filters.dateFilter, filters.startDate, filters.endDate);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!refreshTrigger?.count) return;

    if (refreshTrigger.reset) {
      const defaultFilters = { dateFilter: 'all', startDate: '', endDate: '' };
      setFilters(defaultFilters);
      setShowCustomDate(false);
      fetchErrors(1, 'all', '', '');
    } else {
      fetchErrors(currentPage, filters.dateFilter, filters.startDate, filters.endDate);
    }
  }, [refreshTrigger]);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedError(null);
    setIsModalOpen(false);
    setErrors([]);
    fetchErrors(1, filters.dateFilter, filters.startDate, filters.endDate, tab);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'dateFilter') {
      if (value === "custom") {
        setShowCustomDate(true);
        setFilters(prev => ({ ...prev, dateFilter: value, startDate: '', endDate: '' }));
      } else {
        setShowCustomDate(false);
        setFilters(prev => ({ ...prev, dateFilter: value, startDate: '', endDate: '' }));
      }
    } else {
      setFilters(prev => ({ ...prev, [filterType]: value }));
    }
  };

  const applyFilters = () => {
    if (filters.dateFilter === "custom") {
      if (!filters.startDate || !filters.endDate) {
        toast.error('Please select both start and end dates.');
        return;
      }

      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      if (start > end) {
        toast.error('Start date cannot be after end date.');
        return;
      }
    }

    setCurrentPage(1);
    fetchErrors(1, filters.dateFilter, filters.startDate, filters.endDate);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleErrorClick = (error) => {
    setSelectedError(error);
    setIsModalOpen(true);
  };

  const handleCopy = (value) => {
    if (!value) return;
    if (typeof value === 'object') {
      let copyObject = value;
      if (activeTab !== ERROR_TABS.client) {
        copyObject = {
          _id: value.id ? { $oid: value.id } : undefined,
          error: {
            message: value.errorMessage,
            stack: value.stack,
            name: value.errorName,
            code: value.errorCode
          },
          request: {
            method: value.method,
            url: value.url,
            params: value.params,
            query: value.query,
            body: value.body,
            headers: value.headers,
            ip: value.ip,
            userAgent: value.userAgent
          },
          response: {
            statusCode: value.statusCode
          },
          user: {
            userId: value.userId ? { $oid: value.userId } : null,
            userRole: value.userRole
          },
          system: value.system || null,
          timestamp: value.timestamp,
          createdAt: value.createdAt ? { $date: value.createdAt } : undefined
        };
      }
      navigator.clipboard.writeText(JSON.stringify(copyObject, null, 2));
    } else {
      navigator.clipboard.writeText(String(value));
    }
    toast.success("Copied!");
  };

  const fetchMonitoringStatus = async () => {
    try {
      setLoadingMonitor(true);
      // We use relative URL since our dashboard server now provides this API
      const response = await fetch('/api/monitoring');
      const result = await response.json();
      
      if (result.success) {
        setMonitoringData(result.data);
        setIsMonitorModalOpen(true);
      } else {
        toast.error(result.message || 'Failed to fetch monitoring status');
      }
    } catch (error) {
      console.error('Error fetching monitor status:', error);
      toast.error('Error connecting to monitoring service');
    } finally {
      setLoadingMonitor(false);
    }
  };

  return (
    <div className="server-error-page">
      <div className="server-error-header">
        <div className="server-error-title">
          <h1>Server Errors</h1>
          <p>Backend API failures and auto-reported app errors from Firebase RTDB</p>
        </div>
        <button 
          className={`app-monitor-btn ${loadingMonitor ? 'loading' : ''}`}
          onClick={fetchMonitoringStatus}
          disabled={loadingMonitor}
        >
          <Activity size={18} />
          <span>App Monitor</span>
        </button>
      </div>

      <div className="server-error-tabs">
        <button
          type="button"
          className={`server-error-tab ${activeTab === ERROR_TABS.backend ? 'active' : ''}`}
          onClick={() => handleTabChange(ERROR_TABS.backend)}
        >
          <Server size={16} />
          Backend Errors
        </button>
        <button
          type="button"
          className={`server-error-tab ${activeTab === ERROR_TABS.client ? 'active' : ''}`}
          onClick={() => handleTabChange(ERROR_TABS.client)}
        >
          <Smartphone size={16} />
          App Error Reports
        </button>
      </div>

      <div className="server-error-filter-section">
        <div className="server-error-filter-form">
          <div className="server-error-filter-group">
            <label>Date Range</label>
            <select
              value={filters.dateFilter}
              onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
              className="server-error-filter-select"
            >
              <option value="all">All</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="custom">Custom Date</option>
            </select>
          </div>

          {showCustomDate && (
            <>
              <div className="server-error-filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={filters.startDate ? filters.startDate.split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="server-error-filter-input"
                />
              </div>

              <div className="server-error-filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={filters.endDate ? filters.endDate.split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="server-error-filter-input"
                />
              </div>
            </>
          )}

          <button onClick={applyFilters} className="server-error-apply-btn">
            Apply Filters
          </button>
        </div>
      </div>

      <div className="server-error-table-container">
        {loading ? (
          <div className="server-error-loading">
            <div className="server-error-spinner"></div>
            <p>{activeTab === ERROR_TABS.client ? 'Loading app error reports...' : 'Loading server errors...'}</p>
          </div>
        ) : requestError ? (
          <div className="server-error-error-state">
            <p>{requestError}</p>
            <button
              onClick={() => fetchErrors(currentPage, filters.dateFilter, filters.startDate, filters.endDate)}
              className="server-error-retry-btn"
            >
              Retry
            </button>
          </div>
        ) : errors.length === 0 ? (
          <div className="server-error-empty">
            <p>{activeTab === ERROR_TABS.client ? 'No app error reports found' : 'No server errors found'}</p>
          </div>
        ) : (
          <>
            <div className="server-error-table-wrapper">
              <table className="server-error-table">
                <thead>
                  <tr>
                    {activeTab === ERROR_TABS.client ? (
                      <>
                        <th>Category</th>
                        <th>Screen</th>
                        <th>App</th>
                        <th>Date & Time</th>
                        <th>Status</th>
                        <th>Message</th>
                      </>
                    ) : (
                      <>
                        <th>Title</th>
                        <th>App</th>
                        <th>Date & Time</th>
                        <th>Status</th>
                        <th>Message</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {errors.map((error) => (
                    <tr key={error.id}>
                      {activeTab === ERROR_TABS.client ? (
                        <>
                          <td className="server-error-app-text">
                            <span
                              className="server-error-name-link"
                              onClick={() => handleErrorClick(error)}
                            >
                              {error.errorCategory || error.errorName || 'api_error'}
                            </span>
                          </td>
                          <td className="server-error-app-text">{error.pageName || error.pageContext?.screen || '-'}</td>
                          <td className="server-error-app-text">{error.app || '-'}</td>
                          <td className="server-error-app-text">{error.timestamp || error.reportedAt || '-'}</td>
                          <td className="server-error-app-text">{error.statusCode || '-'}</td>
                          <td className="server-error-message-cell">
                            <span className="server-error-message-text" title={error.errorMessage}>
                              {error.errorMessage || '-'}
                            </span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="server-error-app-text">
                            <span
                              className="server-error-name-link"
                              onClick={() => handleErrorClick(error)}
                            >
                              {error.errorName}
                            </span>
                          </td>
                          <td className="server-error-app-text">{error.app}</td>
                          <td className="server-error-app-text">{error.timestamp || '-'}</td>
                          <td className="server-error-app-text">{error.statusCode || '-'}</td>
                          <td className="server-error-message-cell">
                            <span className="server-error-message-text" title={error.errorMessage}>
                              {error.errorMessage}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="server-error-pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage || loading}
                  className="server-error-pagination-btn"
                >
                  Previous
                </button>

                <span className="server-error-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage || loading}
                  className="server-error-pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isModalOpen && selectedError && (
        <div className="server-error-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="server-error-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="server-error-modal-header">
              <h2>{activeTab === ERROR_TABS.client ? 'App Error Report' : 'Error Details'}</h2>
              <div className="server-error-modal-header-actions">
                <button
                  type="button"
                  className="server-error-modal-copy-btn"
                  onClick={() => handleCopy(selectedError)}
                  title="Copy All Details"
                >
                  <Copy size={14} />
                  <span>Copy</span>
                </button>
              </div>
              <button className="server-error-modal-close" onClick={() => setIsModalOpen(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="server-error-modal-body">
              {activeTab === ERROR_TABS.client ? (
                <>
                  <div className="server-error-detail-box">
                    <span className="server-error-detail-box-header">Category</span>
                    <div className="server-error-detail-box-content server-error-title-content">
                      {selectedError.errorCategory || selectedError.errorName || '-'}
                    </div>
                  </div>

                  <div className="server-error-detail-row-container">
                    <div className="server-error-detail-box half-width">
                      <span className="server-error-detail-box-header">Reported At</span>
                      <div className="server-error-detail-box-content">
                        {selectedError.reportedAt || selectedError.timestamp || '-'}
                      </div>
                    </div>
                    <div className="server-error-detail-box half-width">
                      <span className="server-error-detail-box-header">Status Code</span>
                      <div className="server-error-detail-box-content">{selectedError.statusCode || '-'}</div>
                    </div>
                  </div>

                  <div className="server-error-detail-box">
                    <span className="server-error-detail-box-header">Device & App</span>
                    <div className="server-error-simple-grid">
                      <div className="server-error-simple-row">
                        <span className="server-error-simple-key">App</span>
                        <span className="server-error-simple-value">{selectedError.app || '-'}</span>
                      </div>
                      <div className="server-error-simple-row">
                        <span className="server-error-simple-key">Platform</span>
                        <span className="server-error-simple-value">{selectedError.platform || '-'}</span>
                      </div>
                      <div className="server-error-simple-row">
                        <span className="server-error-simple-key">Version</span>
                        <span className="server-error-simple-value">
                          {selectedError.appVersion
                            ? `${selectedError.appVersion}${selectedError.buildNumber ? ` (${selectedError.buildNumber})` : ''}`
                            : '-'}
                        </span>
                      </div>
                      <div className="server-error-simple-row">
                        <span className="server-error-simple-key">Device ID</span>
                        <span className="server-error-simple-value">{selectedError.deviceId || '-'}</span>
                      </div>
                      <div className="server-error-simple-row">
                        <span className="server-error-simple-key">Screen</span>
                        <span className="server-error-simple-value">
                          {selectedError.pageName || selectedError.pageContext?.screen || '-'}
                        </span>
                      </div>
                      <div className="server-error-simple-row">
                        <span className="server-error-simple-key">API Path</span>
                        <span className="server-error-simple-value">
                          {selectedError.url || selectedError.pageContext?.apiPath || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {selectedError.errorMessage && (
                    <div className="server-error-detail-box">
                      <span className="server-error-detail-box-header">Error Message</span>
                      <div className="server-error-detail-box-content server-error-message-content">
                        {selectedError.errorMessage}
                      </div>
                    </div>
                  )}

                  {selectedError.userId && (
                    <div className="server-error-detail-box">
                      <span className="server-error-detail-box-header">User</span>
                      <div className="server-error-simple-grid">
                        <div className="server-error-simple-row">
                          <span className="server-error-simple-key">User ID</span>
                          <span className="server-error-simple-value">{selectedError.userId}</span>
                        </div>
                        {selectedError.authType && (
                          <div className="server-error-simple-row">
                            <span className="server-error-simple-key">Auth Type</span>
                            <span className="server-error-simple-value">{selectedError.authType}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {hasJsonContent(selectedError.pageContext) && (
                    <JsonDetailSection
                      label="Page Context"
                      data={selectedError.pageContext}
                    />
                  )}

                  {hasJsonContent(selectedError.apiFailure) && (
                    <JsonDetailSection
                      label="API Failure"
                      data={selectedError.apiFailure}
                    />
                  )}

                  {hasJsonContent(selectedError.frontend) && (
                    <JsonDetailSection
                      label="Frontend"
                      data={selectedError.frontend}
                      small
                    />
                  )}

                  {hasJsonContent(selectedError.auth) && (
                    <JsonDetailSection
                      label="Auth (masked)"
                      data={selectedError.auth}
                      small
                    />
                  )}

                  {hasJsonContent(selectedError.structuredError) && (
                    <JsonDetailSection
                      label="Structured Error"
                      data={selectedError.structuredError}
                    />
                  )}

                  {hasJsonContent(selectedError.sharedPreferences) && (
                    <JsonDetailSection
                      label="Shared Preferences"
                      data={selectedError.sharedPreferences}
                      small
                    />
                  )}

                  {hasJsonContent(selectedError.localStorage) && (
                    <JsonDetailSection
                      label="Local Storage"
                      data={selectedError.localStorage}
                      small
                    />
                  )}
                </>
              ) : (
                <>
              <div className="server-error-detail-box">
                <span className="server-error-detail-box-header">Title</span>
                <div className="server-error-detail-box-content server-error-title-content">
                  {selectedError.errorName || '-'}
                </div>
              </div>

              <div className="server-error-detail-row-container">
                <div className="server-error-detail-box half-width">
                  <span className="server-error-detail-box-header">Date & Time</span>
                  <div className="server-error-detail-box-content">{selectedError.timestamp || '-'}</div>
                </div>
                <div className="server-error-detail-box half-width">
                  <span className="server-error-detail-box-header">Status</span>
                  <div className="server-error-detail-box-content">{selectedError.statusCode || '-'}</div>
                </div>
              </div>

              <div className="server-error-detail-box">
                <span className="server-error-detail-box-header">Request</span>
                <div className="server-error-simple-grid">
                  <div className="server-error-simple-row">
                    <span className="server-error-simple-key">App</span>
                    <span className="server-error-simple-value">{selectedError.app || '-'}</span>
                  </div>
                  <div className="server-error-simple-row">
                    <span className="server-error-simple-key">Method</span>
                    <span className="server-error-simple-value">{selectedError.method || '-'}</span>
                  </div>
                  <div className="server-error-simple-row">
                    <span className="server-error-simple-key">URL</span>
                    <span className="server-error-simple-value">{selectedError.url || '-'}</span>
                  </div>
                  <div className="server-error-simple-row">
                    <span className="server-error-simple-key">IP</span>
                    <span className="server-error-simple-value">{selectedError.ip || '-'}</span>
                  </div>
                  {selectedError.userAgent && (
                    <div className="server-error-simple-row">
                      <span className="server-error-simple-key">User Agent</span>
                      <span className="server-error-simple-value">{selectedError.userAgent}</span>
                    </div>
                  )}
                </div>
              </div>

              {hasJsonContent(selectedError.params) && (
                <JsonDetailSection
                  label="Params"
                  data={selectedError.params}
                  small
                />
              )}

              {hasJsonContent(selectedError.query) && (
                <JsonDetailSection
                  label="Query"
                  data={selectedError.query}
                  small
                />
              )}

              {hasJsonContent(selectedError.body) && (
                <JsonDetailSection
                  label="Body"
                  data={selectedError.body}
                />
              )}

              {hasJsonContent(selectedError.headers) && (
                <JsonDetailSection
                  label="Headers"
                  data={selectedError.headers}
                  small
                />
              )}

              {selectedError.errorMessage && (
                <div className="server-error-detail-box">
                  <span className="server-error-detail-box-header">Message</span>
                  <div className="server-error-detail-box-content server-error-message-content">
                    {selectedError.errorMessage}
                  </div>
                </div>
              )}

              {selectedError.stack && (
                <div className="server-error-detail-box">
                  <span className="server-error-detail-box-header">Stack Trace</span>
                  <pre className="server-error-stack-trace">{selectedError.stack}</pre>
                </div>
              )}

              {selectedError.userId && (
                <div className="server-error-detail-box">
                  <span className="server-error-detail-box-header">User</span>
                  <div className="server-error-simple-grid">
                    <div className="server-error-simple-row">
                      <span className="server-error-simple-key">User ID</span>
                      <span className="server-error-simple-value">{selectedError.userId}</span>
                    </div>
                    {selectedError.userRole && (
                      <div className="server-error-simple-row">
                        <span className="server-error-simple-key">Role</span>
                        <span className="server-error-simple-value">{selectedError.userRole}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedError.errorCode && (
                <div className="server-error-detail-box">
                  <span className="server-error-detail-box-header">Error Code</span>
                  <div className="server-error-detail-box-content">{selectedError.errorCode}</div>
                </div>
              )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* App Monitor Modal */}
      {isMonitorModalOpen && monitoringData && (
        <div className="server-error-modal-overlay" onClick={() => setIsMonitorModalOpen(false)}>
          <div className="server-error-modal-content monitor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="server-error-modal-header">
              <div className="monitor-modal-title">
                <Activity size={20} />
                <h2>E2E App Monitoring</h2>
              </div>
              <button className="server-error-modal-close" onClick={() => setIsMonitorModalOpen(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="server-error-modal-body">
              <div className="monitor-summary-cards">
                <div className="monitor-card total">
                  <List size={24} />
                  <div className="monitor-card-info">
                    <span className="monitor-card-label">Total Steps</span>
                    <span className="monitor-card-value">{monitoringData.totalSteps || 0}</span>
                  </div>
                </div>
                <div className="monitor-card success">
                  <CheckCircle size={24} />
                  <div className="monitor-card-info">
                    <span className="monitor-card-label">Passed</span>
                    <span className="monitor-card-value">{monitoringData.passed?.length || 0}</span>
                  </div>
                </div>
                <div className="monitor-card failure">
                  <AlertCircle size={24} />
                  <div className="monitor-card-info">
                    <span className="monitor-card-label">Failed</span>
                    <span className="monitor-card-value">{monitoringData.failed?.length || 0}</span>
                  </div>
                </div>
                <div className="monitor-card status">
                  <Clock size={24} />
                  <div className="monitor-card-info">
                    <span className="monitor-card-label">Last Run</span>
                    <span className="monitor-card-value">
                      {monitoringData.completedAt ? new Date(monitoringData.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'In Progress'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="monitor-details-section">
                <div className="monitor-section-header">
                  <h3>Action Status</h3>
                  <span className={`run-status-badge ${monitoringData.runStatus}`}>
                    {monitoringData.runStatus}
                  </span>
                </div>

                <div className="monitor-actions-list">
                  {monitoringData.failed?.length > 0 ? (
                    <div className="monitor-actions-group failed">
                      <h4>Failed Actions</h4>
                      {monitoringData.failed.map((fail, idx) => (
                        <div key={idx} className="monitor-action-item error">
                          <div className="action-item-header">
                            <span className="action-name">{fail.action}</span>
                            {fail.error.split(' | ').find(p => p.startsWith('HTTP')) && (
                              <span className="action-status-inline">
                                {fail.error.split(' | ').find(p => p.startsWith('HTTP'))}
                              </span>
                            )}
                          </div>
                          <div className="action-error-content">
                            {fail.error.split(' | ').map((part, pIdx) => {
                              // Skip the HTTP part as it's now in the header
                              if (part.startsWith('HTTP')) return null;
                              
                              // Skip redundant "Request failed with status code"
                              if (part.includes('Request failed with status code')) return null;

                              if (part.startsWith('Response:')) {
                                const jsonStr = part.replace('Response: ', '');
                                try {
                                  const parsed = JSON.parse(jsonStr);
                                  return (
                                    <div key={pIdx} className="action-error-response">
                                      <strong>Response:</strong>
                                      <pre>{JSON.stringify(parsed, null, 2)}</pre>
                                    </div>
                                  );
                                } catch (e) {
                                  return <div key={pIdx} className="action-error-part">{part}</div>;
                                }
                              }
                              return <div key={pIdx} className="action-error-part">{part}</div>;
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="monitor-all-passed-msg">
                      <CheckCircle size={48} />
                      <p>All core app flows are currently functional.</p>
                      <span>No failures detected in the latest E2E run.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerError;

